import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./Landing.module.css";
import {
  FiSearch, FiMapPin, FiBriefcase, FiTrendingUp,
  FiUsers, FiStar, FiArrowRight, FiCode, FiPenTool,
  FiBarChart2, FiHeart, FiBook, FiSettings, FiDollarSign, FiMessageSquare,
  FiGlobe, FiSmartphone, FiCpu, FiAward, FiLayers, FiCheck
} from "react-icons/fi";

const categories = [
  { icon: <FiCode />, label: "Technology", count: "3,200+ jobs", color: "#5540C7" },
  { icon: <FiPenTool />, label: "Design", count: "1,400+ jobs", color: "#8a75f6" },
  { icon: <FiBarChart2 />, label: "Marketing", count: "2,100+ jobs", color: "#240BAD" },
  { icon: <FiDollarSign />, label: "Finance", count: "1,800+ jobs", color: "#5540C7" },
  { icon: <FiHeart />, label: "Healthcare", count: "900+ jobs", color: "#8a75f6" },
  { icon: <FiBook />, label: "Education", count: "700+ jobs", color: "#240BAD" },
  { icon: <FiSettings />, label: "Engineering", count: "2,500+ jobs", color: "#5540C7" },
  { icon: <FiMessageSquare />, label: "Sales", count: "1,200+ jobs", color: "#8a75f6" },
];

const steps = [
  {
    num: "01",
    title: "Create Your Profile",
    desc: "Sign up and build your professional profile. Highlight your skills, experience, and what you're looking for.",
  },
  {
    num: "02",
    title: "Discover Opportunities",
    desc: "Browse thousands of curated jobs from top companies. Use smart filters to find the perfect match.",
  },
  {
    num: "03",
    title: "Apply with One Click",
    desc: "Apply instantly to multiple positions. Track your applications and get real-time status updates.",
  },
  {
    num: "04",
    title: "Land Your Dream Job",
    desc: "Connect directly with hiring managers. Receive offers and start your next chapter with confidence.",
  },
];

const testimonials = [
  {
    name: "Priya Sharma",
    role: "Product Designer at Google",
    text: "Blockenzyme helped me land my dream job in under 3 weeks. The platform is incredibly intuitive and the job matching is spot on.",
    rating: 5,
    avatar: "PS",
    color: "#5540C7",
  },
  {
    name: "Rahul Mehta",
    role: "Senior Engineer at Microsoft",
    text: "I was skeptical at first, but the quality of job listings here is unmatched. Got 5 interview calls in my first week!",
    rating: 5,
    avatar: "RM",
    color: "#240BAD",
  },
  {
    name: "Ananya Patel",
    role: "Data Scientist at Amazon",
    text: "The filter system is amazing — I could narrow down to exactly the kind of remote data science role I wanted. Highly recommend!",
    rating: 5,
    avatar: "AP",
    color: "#8a75f6",
  },
];

const companies = [
  { name: "Google", initial: "G", color: "#4285f4" },
  { name: "Microsoft", initial: "M", color: "#00a4ef" },
  { name: "Amazon", initial: "A", color: "#ff9900" },
  { name: "Flipkart", initial: "F", color: "#2874f0" },
  { name: "Infosys", initial: "I", color: "#007cc3" },
  { name: "NVIDIA", initial: "N", color: "#76b900" },
  { name: "Swiggy", initial: "S", color: "#fc8019" },
  { name: "Razorpay", initial: "R", color: "#2EB872" },
];

const Landing = () => {
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate("/find-jobs");
  };

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <FiTrendingUp /> &nbsp; 50,000+ jobs added this month
          </div>
          <h1 className={styles.heroTitle}>
            Find Your <span className={styles.highlight}>Dream Career</span> Today
          </h1>
          <p className={styles.heroSubtitle}>
            Connect with top employers across India and beyond. Your next big opportunity is just a search away.
          </p>

          <form className={styles.searchBar} onSubmit={handleSearch}>
            <div className={styles.searchInput}>
              <FiSearch className={styles.searchIcon} />
              <input placeholder="Job title, skills, or company" />
            </div>
            <div className={styles.searchDivider} />
            <div className={styles.searchInput}>
              <FiMapPin className={styles.searchIcon} />
              <input placeholder="City or Remote" />
            </div>
            <button type="submit" className={styles.searchBtn}>
              Search Jobs
            </button>
          </form>

          <div className={styles.popularTags}>
            <span className={styles.tagsLabel}>Popular:</span>
            {["React Developer", "Data Scientist", "Product Manager", "UI/UX Designer"].map(tag => (
              <Link key={tag} to="/find-jobs" className={styles.tag}>{tag}</Link>
            ))}
          </div>
        </div>

        <div className={styles.heroVisual}>
          <div className={styles.heroCard}>
            <div className={styles.heroCardHeader}>
              <div className={styles.companyBadge} style={{ background: "#EEF5FE", color: "#5540C7" }}>G</div>
              <div>
                <div className={styles.jobTitle}>Senior Designer</div>
                <div className={styles.companyName}>Google • Bangalore</div>
              </div>
              <span className={styles.salaryBadge}>₹1.2L/mo</span>
            </div>
            <div className={styles.tagRow}>
              <span>Full Time</span>
              <span>Hybrid</span>
              <span>Figma</span>
            </div>
          </div>

          <div className={styles.heroCard} style={{ marginLeft: "2rem", marginTop: "0.5rem" }}>
            <div className={styles.heroCardHeader}>
              <div className={styles.companyBadge} style={{ background: "#fff8e6", color: "#ff9900" }}>A</div>
              <div>
                <div className={styles.jobTitle}>Data Scientist</div>
                <div className={styles.companyName}>Amazon • Remote</div>
              </div>
              <span className={styles.salaryBadge}>₹1.5L/mo</span>
            </div>
            <div className={styles.tagRow}>
              <span>Full Time</span>
              <span>Remote</span>
              <span>Python</span>
            </div>
          </div>

          <div className={styles.heroCard} style={{ marginTop: "0.5rem" }}>
            <div className={styles.heroCardHeader}>
              <div className={styles.companyBadge} style={{ background: "#e8f5e9", color: "#2EB872" }}>R</div>
              <div>
                <div className={styles.jobTitle}>Full Stack Engineer</div>
                <div className={styles.companyName}>Razorpay • Mumbai</div>
              </div>
              <span className={styles.salaryBadge}>₹1.0L/mo</span>
            </div>
            <div className={styles.tagRow}>
              <span>Full Time</span>
              <span>On-site</span>
              <span>React</span>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className={styles.statsBar}>
        <div className={styles.statsInner}>
          <div className={styles.stat}>
            <span className={styles.statNumber}>50,000+</span>
            <span className={styles.statLabel}>Active Jobs</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.stat}>
            <span className={styles.statNumber}>10,000+</span>
            <span className={styles.statLabel}>Companies</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.stat}>
            <span className={styles.statNumber}>5,00,000+</span>
            <span className={styles.statLabel}>Candidates</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.stat}>
            <span className={styles.statNumber}>95%</span>
            <span className={styles.statLabel}>Success Rate</span>
          </div>
        </div>
      </section>

      {/* ─── WHAT WE OFFER ─── */}
      <section className={styles.offerSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Everything We Do</h2>
            <p className={styles.sectionSubtitle}>
              One platform — three powerful services built for India's tech ecosystem.
            </p>
          </div>

          {/* 1 — Dev Services */}
          <div className={styles.offerRow}>
            <div className={styles.offerContent}>
              <div className={styles.offerEyebrow} style={{ color: "#5540C7", background: "rgba(85,64,199,0.08)" }}>
                <FiGlobe /> Dev Services
              </div>
              <h3 className={styles.offerTitle}>Apps &amp; Websites Built for You</h3>
              <p className={styles.offerDesc}>
                We design and build production-ready web apps, mobile apps, blockchain platforms, and AI-powered solutions — at low cost and with full code ownership handed to you.
              </p>
              <ul className={styles.offerPoints}>
                {["Web &amp; Mobile App Development", "Blockchain / Web3 Products", "AI-Powered Solutions", "Transparent pricing from ₹89,999"].map((pt, i) => (
                  <li key={i}><FiCheck className={styles.offerCheck} /><span dangerouslySetInnerHTML={{ __html: pt }} /></li>
                ))}
              </ul>
              <Link to="/dev-services" className={styles.offerBtn} style={{ background: "#5540C7" }}>
                Explore Dev Services <FiArrowRight />
              </Link>
            </div>
            <div className={styles.offerVisual} style={{ background: "linear-gradient(135deg,#f0ecff 0%,#e0d8ff 100%)" }}>
              <div className={styles.offerIconGrid}>
                {[
                  { icon: <FiGlobe />, label: "Web Apps", color: "#5540C7" },
                  { icon: <FiSmartphone />, label: "Mobile", color: "#240BAD" },
                  { icon: <FiLayers />, label: "Web3", color: "#8a75f6" },
                  { icon: <FiCpu />, label: "AI / ML", color: "#5540C7" },
                ].map((item, i) => (
                  <div key={i} className={styles.offerIconBox}>
                    <span className={styles.offerIconWrap} style={{ color: item.color, background: item.color + "18" }}>{item.icon}</span>
                    <span className={styles.offerIconLabel}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2 — Tech Workshops */}
          <div className={`${styles.offerRow} ${styles.offerRowReverse}`}>
            <div className={styles.offerContent}>
              <div className={styles.offerEyebrow} style={{ color: "#240BAD", background: "rgba(36,11,173,0.08)" }}>
                <FiAward /> Tech Workshops
              </div>
              <h3 className={styles.offerTitle}>Industry-Grade Training for Colleges</h3>
              <p className={styles.offerDesc}>
                We bring hands-on tech workshops to engineering and science colleges across India — delivered by working professionals, not just academics.
              </p>
              <ul className={styles.offerPoints}>
                {["Web Dev, AI/ML, Blockchain &amp; more", "Hands-on projects &amp; real outcomes", "Certificates students can show on LinkedIn", "Customisable to your college's syllabus"].map((pt, i) => (
                  <li key={i}><FiCheck className={styles.offerCheck} /><span dangerouslySetInnerHTML={{ __html: pt }} /></li>
                ))}
              </ul>
              <Link to="/tech-workshops" className={styles.offerBtn} style={{ background: "#240BAD" }}>
                View Workshops <FiArrowRight />
              </Link>
            </div>
            <div className={styles.offerVisual} style={{ background: "linear-gradient(135deg,#eef5fe 0%,#d8e8ff 100%)" }}>
              <div className={styles.workshopStats}>
                <div className={styles.wStat}><span className={styles.wStatNum}>500+</span><span className={styles.wStatLabel}>Students Trained</span></div>
                <div className={styles.wStat}><span className={styles.wStatNum}>10+</span><span className={styles.wStatLabel}>Colleges</span></div>
                <div className={styles.wStat}><span className={styles.wStatNum}>6</span><span className={styles.wStatLabel}>Topics</span></div>
                <div className={styles.wStat}><span className={styles.wStatNum}>4.9★</span><span className={styles.wStatLabel}>Rating</span></div>
              </div>
            </div>
          </div>

          {/* 3 — Career Jobs */}
          <div className={styles.offerRow}>
            <div className={styles.offerContent}>
              <div className={styles.offerEyebrow} style={{ color: "#8a75f6", background: "rgba(138,117,246,0.1)" }}>
                <FiBriefcase /> Career Jobs
              </div>
              <h3 className={styles.offerTitle}>Find Jobs &amp; Hire Top Talent</h3>
              <p className={styles.offerDesc}>
                Connect with 50,000+ active job listings from India's top companies — or post roles to find the right candidates fast. Your next career move starts here.
              </p>
              <ul className={styles.offerPoints}>
                {["50,000+ curated job listings", "Smart filters by role, location &amp; salary", "One-click apply with real-time tracking", "Hire talent across every tech domain"].map((pt, i) => (
                  <li key={i}><FiCheck className={styles.offerCheck} /><span dangerouslySetInnerHTML={{ __html: pt }} /></li>
                ))}
              </ul>
              <div className={styles.offerBtnGroup}>
                <Link to="/find-jobs" className={styles.offerBtn} style={{ background: "#8a75f6" }}>
                  Find Jobs <FiArrowRight />
                </Link>
                <Link to="/hire-employees" className={styles.offerBtnOutline} style={{ borderColor: "#8a75f6", color: "#8a75f6" }}>
                  Hire Talent <FiArrowRight />
                </Link>
              </div>
            </div>
            <div className={styles.offerVisual} style={{ background: "linear-gradient(135deg,#f5f0ff 0%,#ede8ff 100%)" }}>
              <div className={styles.jobPreview}>
                {[
                  { role: "Frontend Engineer", co: "Razorpay • Mumbai", sal: "₹1.0L/mo", col: "#2EB872" },
                  { role: "Data Scientist", co: "Amazon • Remote", sal: "₹1.5L/mo", col: "#ff9900" },
                  { role: "Product Manager", co: "Flipkart • Bangalore", sal: "₹1.8L/mo", col: "#2874f0" },
                ].map((job, i) => (
                  <div key={i} className={styles.jobPreviewCard}>
                    <div className={styles.jobPreviewDot} style={{ background: job.col + "22", color: job.col }}>{job.co[0]}</div>
                    <div className={styles.jobPreviewInfo}>
                      <div className={styles.jobPreviewRole}>{job.role}</div>
                      <div className={styles.jobPreviewCo}>{job.co}</div>
                    </div>
                    <div className={styles.jobPreviewSal}>{job.sal}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Categories Section */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Browse by Category</h2>
            <p className={styles.sectionSubtitle}>Explore opportunities across every industry and role</p>
          </div>
          <div className={styles.categoriesGrid}>
            {categories.map((cat) => (
              <Link to="/find-jobs" key={cat.label} className={styles.categoryCard}>
                <span className={styles.categoryIcon} style={{ color: cat.color, background: cat.color + "18" }}>
                  {cat.icon}
                </span>
                <h4 className={styles.categoryLabel}>{cat.label}</h4>
                <p className={styles.categoryCount}>{cat.count}</p>
                <FiArrowRight className={styles.categoryArrow} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>How Blockenzyme Works</h2>
            <p className={styles.sectionSubtitle}>Your journey from job search to offer letter in 4 simple steps</p>
          </div>
          <div className={styles.stepsGrid}>
            {steps.map((step, i) => (
              <div key={i} className={styles.stepCard}>
                <div className={styles.stepNum}>{step.num}</div>
                <h4 className={styles.stepTitle}>{step.title}</h4>
                <p className={styles.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className={styles.section} style={{ background: "#f8fbff" }}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Trusted by Leading Companies</h2>
            <p className={styles.sectionSubtitle}>Join thousands of professionals working at India's top employers</p>
          </div>
          <div className={styles.companiesGrid}>
            {companies.map((company) => (
              <div key={company.name} className={styles.companyCard}>
                <div
                  className={styles.companyInitial}
                  style={{ background: company.color + "18", color: company.color }}
                >
                  {company.initial}
                </div>
                <span className={styles.companyCardName}>{company.name}</span>
              </div>
            ))}
          </div>
          <div className={styles.centerBtn}>
            <Link to="/find-jobs" className={styles.outlineBtn}>
              View All Companies <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Success Stories</h2>
            <p className={styles.sectionSubtitle}>Hear from professionals who found their dream job with us</p>
          </div>
          <div className={styles.testimonialsGrid}>
            {testimonials.map((t, i) => (
              <div key={i} className={styles.testimonialCard}>
                <div className={styles.stars}>
                  {Array(t.rating).fill(0).map((_, j) => (
                    <FiStar key={j} className={styles.star} />
                  ))}
                </div>
                <p className={styles.testimonialText}>"{t.text}"</p>
                <div className={styles.testimonialAuthor}>
                  <div
                    className={styles.testimonialAvatar}
                    style={{ background: t.color + "18", color: t.color }}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <div className={styles.testimonialName}>{t.name}</div>
                    <div className={styles.testimonialRole}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>
            Ready to Work with Blockenzyme?
          </h2>
          <p className={styles.ctaSubtitle}>
            Whether you need a product built, a workshop for your college, or your next career opportunity — we've got you covered.
          </p>
          <div className={styles.ctaBtns}>
            <Link to="/dev-services" className={styles.ctaPrimaryBtn}>
              <FiGlobe /> Build a Product
            </Link>
            <Link to="/tech-workshops" className={styles.ctaSecondaryBtn}>
              <FiAward /> Book a Workshop
            </Link>
            <Link to="/find-jobs" className={styles.ctaSecondaryBtn}>
              <FiBriefcase /> Find Jobs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
